/* eslint-disable react/jsx-no-bind -- one-line forwarding to non-memo'd children; component-anatomy.md lists this as a narrow exception */

import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { styled, useTheme } from '@mui/material/styles'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import React, { useState, Children } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useLocation, useNavigate, matchPath } from 'react-router-dom-v5-compat'

import { useReviewAccess } from 'components/exceptions/hooks/useReviewAccess'
import Constants from 'Constants'
import { selectTenantSettings } from 'data/tenant/tenantSettingsSelectors'
import { Z_INDEX } from 'theme/zIndex'

import { getAllMenuItems } from './menuItems'
import NavItem from './NavItem'

const { MEDIA_SERVER } = Constants
const { MENU } = Z_INDEX

const MenuIcon = styled(`img`)`
  margin-right: 8px;
  size: 10px;
`

const NavigationMenu = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const tenantSettings = useSelector(selectTenantSettings, shallowEqual)
  const { isReviewEnabled } = useReviewAccess()
  const { pathname } = location

  const [anchorElNav, setAnchorElNav] = useState<HTMLElement | null>(null)

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = (): void => {
    setAnchorElNav(null)
  }

  const handleNavigate = (route: string): void => {
    navigate(route, { replace: true })
    handleCloseNavMenu()
  }

  const menuItems = getAllMenuItems(tenantSettings, isReviewEnabled).filter(
    ({ permission, isFeatureAvailable }) => permission && isFeatureAvailable
  )

  const activeIndex = React.useMemo(
    () =>
      menuItems.findIndex(({ childList = [] }) =>
        childList.some(childPath => matchPath({ path: childPath, caseSensitive: false, end: false }, location.pathname))
      ),
    [menuItems, location.pathname]
  )

  return (
    <>
      <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'none', md: 'flex' } }}>
        <Tabs
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.background.paper,
            },
          }}
          textColor="primary"
          value={activeIndex}
        >
          {Children.toArray(
            menuItems.map((menuItem, index) => {
              const isActive = menuItem.childList.includes(pathname)
              const { title, route, icon, IconComponent, iconFilter } = menuItem
              return (
                <NavItem
                  icon={icon}
                  IconComponent={IconComponent}
                  iconFilter={iconFilter}
                  index={index}
                  isActive={isActive}
                  label={title}
                  to={route}
                />
              )
            })
          )}
        </Tabs>
      </Box>
      <Box sx={{ flexGrow: 1, display: { xs: 'flex', sm: 'flex', md: 'none' } }}>
        <IconButton
          aria-controls="menu-appbar"
          aria-haspopup="true"
          aria-label="account of current user"
          color="inherit"
          onClick={handleOpenNavMenu}
        >
          <img alt="menu" src={`${MEDIA_SERVER}HambugerMenu.svg`} />
        </IconButton>
      </Box>
      <Menu
        keepMounted
        anchorEl={anchorElNav}
        id="menu-appbar"
        open={Boolean(anchorElNav)}
        sx={{ zIndex: MENU }}
        onClose={handleCloseNavMenu}
      >
        {menuItems.map(({ icon, IconComponent, title, route }, i) => {
          return (
            <MenuItem key={title} divider={i < menuItems.length - 1} onClick={() => handleNavigate(route)}>
              {IconComponent ? (
                <IconComponent fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              ) : (
                <MenuIcon alt={icon} src={MEDIA_SERVER + icon} />
              )}
              <Typography variant="body1">{title}</Typography>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default NavigationMenu
